import { useLayoutEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTeamTheme } from '@mondy/shared';
import type { TeamPublic, TeamTheme } from '@mondy/shared';
import { api } from '@/api';
import { loadTeamFontStylesheet } from '@/lib/font-loading';
import { useCurrentUser } from './useCurrentUser';

interface TeamsResponse {
  teams: TeamPublic[];
}

/**
 * Perceived luminance of a `#RRGGBB` color in 0..1. Uses the same Rec. 601
 * coefficients as the design handoff's `contrastInkFor` helper so the
 * black/white split lands on the same teams across web + native + the
 * design canvas.
 */
function perceivedLuminance(hex: string): number {
  const c = hex.replace('#', '');
  if (c.length !== 6) return 0;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/**
 * Resolve the current user's favorite team into a `TeamTheme` by joining
 * `UserPublic.favoriteTeamId` against `/api/teams`. Returns a stable
 * `{ team, theme, isLoading }` tuple — null `team`/`theme` when the user
 * has no favorite. `isLoading` is true while the auth check is pending OR
 * while `/api/teams` is in flight for a user that has a favorite (so the
 * caller can hold off rendering until the theme is ready).
 *
 * Note: TanStack Query's `isLoading` is `false` when `enabled: false`, so
 * we derive the loading flag manually instead of forwarding it.
 *
 * The `/api/teams` query is cached for 1h, so this adds no real fetch cost.
 */
export function useFavoriteTeamTheme(): {
  team: TeamPublic | null;
  theme: TeamTheme | null;
  isLoading: boolean;
} {
  const user = useCurrentUser();
  const userPending = user === undefined;
  const favoriteTeamId = user ? user.favoriteTeamId : null;

  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get<TeamsResponse>('/api/teams'),
    staleTime: 60 * 60 * 1000,
    // Only fetch teams when we have a favorite — FavoriteTeamCard / Teams
    // page already warm this cache, so we rarely pay the fetch either way.
    enabled: !!favoriteTeamId,
  });

  return useMemo(() => {
    // `!teams.isError` lets a failed `/api/teams` settle the gate — without
    // it the app would hang on the loader forever if teams never resolve
    // (5xx, network drop). Degrades to the neutral palette in that case.
    const isLoading =
      userPending || (!!favoriteTeamId && !teams.data && !teams.isError);
    if (!favoriteTeamId || !teams.data) {
      return { team: null, theme: null, isLoading };
    }
    const team = teams.data.teams.find((t) => t.id === favoriteTeamId) ?? null;
    if (!team) return { team: null, theme: null, isLoading: false };
    return { team, theme: getTeamTheme(team.fifaCode), isLoading: false };
  }, [favoriteTeamId, teams.data, teams.isError, userPending]);
}

/**
 * Writes team-theme CSS custom properties to `<html>` whenever the user's
 * favorite team resolves to a `TeamTheme`. Neutral defaults from `:root`
 * take over on cleanup (when favorite cleared or theme missing).
 *
 * Uses `useLayoutEffect` so the variables are committed before the browser
 * paints — without this, the first frame on which the App body mounts
 * would render against the neutral fallback palette and flash to the team
 * palette one frame later. App.tsx pairs this with a loading gate so the
 * effect only runs once theme data is actually ready.
 *
 * Must be mounted once at the app root — App.tsx does this.
 */
export function useApplyTeamThemeVars(theme: TeamTheme | null): void {
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!theme) {
      // Reset any previously-applied overrides so :root defaults win.
      root.style.removeProperty('--team-primary');
      root.style.removeProperty('--team-accent');
      root.style.removeProperty('--team-deep');
      root.style.removeProperty('--team-ink');
      root.style.removeProperty('--team-tint');
      root.style.removeProperty('--team-font');
      root.style.removeProperty('--team-bg-strength');
      root.style.removeProperty('--team-on-primary');
      return;
    }
    const { palette } = theme;
    const isLight = palette.mode === 'light';
    // Light-mode teams: using palette.deep as the base color turns pages
    // blinding-white. Swap to palette.ink per the handoff MBg logic.
    const deep = isLight ? palette.ink ?? '#0b0b0d' : palette.deep;
    // Foreground for solid `--team-primary` surfaces (e.g. .btn-primary).
    // Picks ink-vs-white by perceived luminance of the primary color (not
    // by `palette.mode`) — Brazil/Saudi/Senegal are technically `mode:
    // 'dark'` teams, but their primary is bright yellow/gold and needs
    // dark text. Threshold + formula match the design handoff's
    // `contrastInkFor` helper in `landmark-picks.js`.
    const onPrimary = perceivedLuminance(palette.primary) > 0.6
      ? palette.ink ?? '#0b0b0d'
      : '#ffffff';

    root.style.setProperty('--team-primary', palette.primary);
    root.style.setProperty('--team-accent', palette.accent);
    root.style.setProperty('--team-deep', deep);
    root.style.setProperty('--team-ink', palette.ink ?? palette.deep);
    root.style.setProperty('--team-tint', palette.tint);
    root.style.setProperty('--team-on-primary', onPrimary);
    root.style.setProperty(
      '--team-font',
      `"${theme.font}", "Bebas Neue", Impact, "Haettenschweiler", sans-serif`,
    );
    root.style.setProperty('--team-bg-strength', '1');
    loadTeamFontStylesheet(theme.font);

    return () => {
      root.style.removeProperty('--team-primary');
      root.style.removeProperty('--team-accent');
      root.style.removeProperty('--team-deep');
      root.style.removeProperty('--team-ink');
      root.style.removeProperty('--team-tint');
      root.style.removeProperty('--team-font');
      root.style.removeProperty('--team-bg-strength');
      root.style.removeProperty('--team-on-primary');
    };
  }, [theme]);
}
